// src/pages/DeviceIntegrations.tsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "@/components/AuthLayout";
import Button from "@/components/Button";
import ProgressBar from "@/components/ProgressBar";
import { getSupabase, currentUser } from "@/lib/supabase";

export default function DeviceIntegrations() {
  const navigate = useNavigate();
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  if (params.get("whoop") === "connected") {
    alert("WHOOP connected successfully!");
    // Clean the URL so it doesn't re-trigger on refresh
    window.history.replaceState({}, "", "/device-integrations");
  }
}, []);

  const SUPABASE_EDGE_URL = "https://usbamprqshmoalvlkjch.supabase.co/functions/v1";

  const handleConnect = async (type: "garmin" | "whoop") => {
  if (!consent) {
    setError("You must consent to connect your wearable.");
    return;
  }
  setSaving(true);
  setError(null);
  try {
    const user = await currentUser();
    if (!user?.id) throw new Error("Not logged in");
    const supabase = getSupabase();
    await supabase
      .from("clients")
      .update({
        wearable_data_consent: true,
        wearable_data_consent_at: new Date().toISOString(),
      })
      .eq("user_id", user.id);

    const { data: profile } = await supabase
      .from("clients")
      .select("phone")
      .eq("user_id", user.id)
      .single();

    if (!profile?.phone) throw new Error("Phone number missing");

    const phone = encodeURIComponent(profile.phone.replace(/\s/g, ''));

    let url: string;
    if (type === "garmin") {
      url = `${SUPABASE_EDGE_URL}/garmin-auth?phone=${phone}`;
    } else {
      // WHOOP: Add success_redirect
      const successRedirect = encodeURIComponent(
  "https://www.inputperformanceai.com/device-integrations?whoop=connected"
);
      url = `${SUPABASE_EDGE_URL}/whoop-auth?phone=${phone}&success_redirect=${successRedirect}`;
    }

    window.location.href = url;
  } catch (err: any) {
    setError(err.message || "Connection failed.");
    setSaving(false);
  }
};

  const handleSkip = () => {
    navigate("/create-goal");
  };

  return (
    <AuthLayout>
      <div className="absolute top-4 left-4 z-10">
  <button
    onClick={() => navigate(-1)}
    className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-[#0f172a] transition-colors"
  >
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
    Back
  </button>
</div>
      
      <div className="space-y-8 text-center max-w-lg mx-auto">
        <div>
          <h1 className="text-3xl font-bold text-[#0f172a]">Connect Your Wearables</h1>
          <p className="text-gray-500 mt-2">Optional — for smarter coaching</p>
        </div>

        {/* REQUIRED CONSENT — YOUR EXACT TEXT */}
        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 text-left">
          <label className="flex items-start gap-4 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="w-6 h-6 mt-0.5 rounded border-gray-700 text-green-500 focus:ring-green-500 bg-gray-800"
            />
            <div className="text-sm leading-tight">
              <strong className="text-[#0f172a]">
                I consent to Input accessing and processing my Garmin Connect™ and/or WHOOP data — only the data I authorise — including heart rate, sleep, activity, recovery and related metrics, to personalise my coaching. I understand I can revoke this access at any time.
              </strong>
              <br /><br />
              <span className="text-xs text-gray-500">
                Your data is encrypted, processed securely, and never sold. Access can be revoked anytime in Settings.
              </span>
            </div>
          </label>
        </div>

        {error && <p className="text-red-400 text-sm">{error}</p>}

        {/* Connect Buttons — Official Brand Colors */}
        <div className="space-y-4">
          {/* GARMIN */}
          <button
            onClick={() => handleConnect("garmin")}
            disabled={!consent || saving}
            className={`
              w-full h-16 rounded-2xl font-medium transition-all duration-300 flex items-center justify-between px-6 shadow-lg
              ${consent && !saving
                ? "bg-[#00AEEF] hover:bg-[#0099D6] text-[#0f172a] shadow-[#00AEEF]/50 ring-2 ring-[#00AEEF]/50"
                : "bg-gray-800 text-gray-500 cursor-not-allowed border-2 border-gray-700"
              }
            `}
          >
            <div className="w-32 h-11 rounded-lg overflow-hidden bg-black/10 flex items-center justify-center px-3">
              <img
                src="https://usbamprqshmoalvlkjch.supabase.co/storage/v1/object/public/branding/GARMINWHITERES.png"
                alt="Garmin"
                className="h-full w-auto object-contain"
              />
            </div>
            <span className="text-lg flex-1 text-center font-semibold">
              {consent ? "Connect with Garmin" : "Consent required"}
            </span>
          </button>

          {/* WHOOP */}
          <button
            onClick={() => handleConnect("whoop")}
            disabled={!consent || saving}
            className={`
              w-full h-16 rounded-2xl font-medium transition-all duration-300 flex items-center justify-between px-6 shadow-lg
              ${consent && !saving
                ? "bg-[#a78bfa] hover:bg-[#8b5cf6] text-[#0f172a] shadow-[#a78bfa]/50 ring-2 ring-[#a78bfa]/50"
                : "bg-gray-800 text-gray-500 cursor-not-allowed border-2 border-gray-700"
              }
            `}
          >
            <div className="w-32 h-11 rounded-lg overflow-hidden bg-black/10 flex items-center justify-center px-3">
              <img
                src="https://usbamprqshmoalvlkjch.supabase.co/storage/v1/object/public/branding/WHOOP%20Logo%20White.png"
                alt="WHOOP"
                className="h-full w-auto object-contain"
              />
            </div>
            <span className="text-lg flex-1 text-center font-semibold">
              {consent ? "Connect with WHOOP" : "Consent required"}
            </span>
          </button>
        </div>

        {/* Skip + Continue */}
        <div className="space-y-3">
          <button
            onClick={handleSkip}
            className="text-sm text-gray-500 hover:text-gray-600 underline"
          >
            Skip for now →
          </button>

          <Button
            onClick={handleSkip}
            disabled={saving}
            className="w-full"
          >
            {saving ? "Saving..." : "Continue →"}
          </Button>
        </div>

        <ProgressBar step={8} total={10} />
      </div>
    </AuthLayout>
  );
}
